import { FeatureManagementType } from "@api/apiClient";
import Footer from "@components/Footer/Footer";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import { Box, Typography, useTheme } from "@mui/material";

import { observer } from "mobx-react";
import { useEffect } from "react";
import { Menu, MenuItem, Sidebar, SubMenu, menuClasses, sidebarClasses } from "react-pro-sidebar";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

/** ICT Roles settings page: route remains; toggle to show sidebar link again. ACC Roles stays in the menu. */
const SHOW_SETTINGS_ICT_ROLES_NAV = false;

const AppSettingsPage = observer(() => {
  const appStore = useAppStore();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const sideBarWidth = 300;

  // Navigate to the first available settings page 'if enabled'
  useEffect(() => {
    if (appStore.hasFeature(FeatureManagementType.RebarEstimation)) {
      if (window.location.pathname === "/settings") {
        navigate("/settings/rebar-estimation", { replace: true });
      }
    }
  }, []);

  const isActive = (path: string): boolean => location.pathname === path;

  return (
    <Box sx={{ display: "flex", flexGrow: 1 }}>
      <Sidebar
        rootStyles={{
          borderColor: theme.palette.divider,
          width: `${sideBarWidth}px`,
          [`.${sidebarClasses.container}`]: {
            position: "fixed",
            top: "64px",
            left: 0,
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            width: `${sideBarWidth - 1}px`,
          },
        }}
      >
        <Typography ml={2} mt={2} variant="h6" fontWeight="bold">
          {i18n.t("setting-menu.title.settings")}
        </Typography>
        <Menu
          transitionDuration={1000}
          menuItemStyles={{
            root: {
              fontSize: theme.typography.body1.fontSize,
              fontWeight: theme.typography.body1.fontWeight,
            },
            icon: {
              color: theme.palette.text.primary,
              [`&.${menuClasses.disabled}`]: {
                color: theme.palette.text.disabled,
              },
            },
            SubMenuExpandIcon: {
              color: theme.palette.text.primary,
              "& span": {
                display: "block",
              },
            },
            subMenuContent: () => ({
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.background.default,
            }),
            button: {
              [`&.${menuClasses.disabled}`]: {
                backgroundColor: theme.palette.action.disabled,
                color: theme.palette.text.disabled,
              },
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
              [`&.active`]: {
                backgroundColor: theme.palette.action.selected,
                fontWeight: "bold",
              },
            },
            label: ({ open }) => ({
              fontWeight: open ? 600 : undefined,
            }),
          }}
        >
          <SubMenu label={i18n.t("setting-menu.title.non-model-assumption")}>
            {appStore.hasFeature(FeatureManagementType.RebarEstimation) && (
              <MenuItem component={<Link to="/settings/rebar-estimation" className={isActive("/settings/rebar-estimation") ? "active" : ""} />}>
                {i18n.t("setting-menu.title.rebar-estimation")}
              </MenuItem>
            )}
            {appStore.hasFeature(FeatureManagementType.RebarEstimation) && (
              <MenuItem component={<Link to="/settings/meta-data-management" className={isActive("/settings/meta-data-management") ? "active" : ""} />}>
                {i18n.t("setting-menu.title.meta-data-management")}
              </MenuItem>
            )}
            <MenuItem component={<Link to="/settings/non-modelled-elements" className={isActive("/settings/non-modelled-elements") ? "active" : ""} />}>
              {i18n.t("setting-menu.title.non-model-elements")}
            </MenuItem>
          </SubMenu>
            <SubMenu label={i18n.t("setting-menu.title.material-management")}>
              <MenuItem component={<Link to="/settings/materials" className={isActive("/settings/materials") ? "active" : ""} />}>
                {i18n.t("setting-menu.title.materials")}
              </MenuItem>
              <MenuItem component={<Link to="/settings/auto-mapping" className={isActive("/settings/auto-mapping") ? "active" : ""} />}>
                {i18n.t("setting-menu.title.auto-mapping")}
              </MenuItem>
            </SubMenu>
          <SubMenu label={i18n.t("setting-menu.title.user-management")}>
            <MenuItem component={<Link to="/settings/users" className={isActive("/settings/users") ? "active" : ""} />}>{i18n.t("setting-menu.title.users")}</MenuItem>
            <MenuItem component={<Link to="/settings/admin-users" className={isActive("/settings/admin-users") ? "active" : ""} />}>{i18n.t("setting-menu.title.admin")}</MenuItem>
            <MenuItem component={<Link to="/settings/permissions" className={isActive("/settings/permissions") ? "active" : ""} />}>{i18n.t("setting-menu.title.permissions")}</MenuItem>
            {SHOW_SETTINGS_ICT_ROLES_NAV && (
              <MenuItem component={<Link to="/settings/roles" className={isActive("/settings/roles") ? "active" : ""} />}>{i18n.t("setting-menu.title.roles")}</MenuItem>
            )}
            <MenuItem component={<Link to="/settings/acc-roles" className={isActive("/settings/acc-roles") ? "active" : ""} />}>{i18n.t("setting-menu.title.acc-roles")}</MenuItem>
            <MenuItem component={<Link to="/settings/role-mapping" className={isActive("/settings/role-mapping") ? "active" : ""} />}>{i18n.t("setting-menu.title.role-mapping")}</MenuItem>
          </SubMenu>
          <SubMenu label={i18n.t("setting-menu.title.utilities")}>
            <MenuItem component={<Link to="/settings/design-automation" className={isActive("/settings/design-automation") ? "active" : ""} />}>
              {i18n.t("setting-menu.title.design-automation")}
            </MenuItem>
            <MenuItem component={<Link to="/settings/documents" className={isActive("/settings/documents") ? "active" : ""} />}>{i18n.t("setting-menu.title.document")}</MenuItem>
          </SubMenu>
          {appStore.hasFeature(FeatureManagementType.ModelScopes) && (
            <MenuItem component={<Link to="/settings/model-scopes" className={isActive("/settings/model-scopes") ? "active" : ""} />}>
              {i18n.t("setting-menu.title.model-scopes")}
            </MenuItem>
          )}
          <MenuItem component={<Link to="/settings/profiles" className={isActive("/settings/profiles") ? "active" : ""} />}>{i18n.t("setting-menu.title.profiles")}</MenuItem>
          <SubMenu label={i18n.t("setting-menu.title.insights")}>
            <MenuItem component={<Link to="/settings/unmapped-cad-materials" className={isActive("/settings/not-mapped-material-insight") ? "active" : ""} />}>
              {i18n.t("setting-menu.title.un-mapped-cad-materials")}
            </MenuItem>
            <MenuItem component={<Link to="/settings/co2-material-type-by-category" className={isActive("/settings/co2-material-type-by-category") ? "active" : ""} />}>
              {i18n.t("setting-menu.title.co2-material-type-by-category")}
            </MenuItem>
            <MenuItem component={<Link to="/settings/application-logs" className={isActive("/settings/application-logs") ? "active" : ""} />}>
              {i18n.t("setting-menu.title.application-logs")}
            </MenuItem>
          </SubMenu>
          {appStore.hasFeature(FeatureManagementType.DACategoryFilter) && (
            <MenuItem component={<Link to="/settings/cad-model-category-filter" className={isActive("/settings/cad-model-category-filter") ? "active" : ""} />}>
              {i18n.t("setting-menu.title.cad-model-category-filter")}
            </MenuItem>
          )}
        </Menu>
      </Sidebar>
      <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, flexShrink: 1 }}>
        <Box sx={{ height: "100%", width: `calc(100vw - ${sideBarWidth}px)` }}>
          <Outlet />
        </Box>
        <Box sx={{ display: "flex", flexGrow: 0, alignItems: "center", justifyContent: "center" }}>
          <Footer message={i18n.t("app.footer.title")} />
        </Box>
      </Box>
    </Box>
  );
});

export default AppSettingsPage;
